import { useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Icon, Text, TextInput } from '@hotjar/hotsauce';
import { parseEmailListString, validateEmailList } from '@hotjar/webapp/lib/email';
import { ErrorBoundary } from '@hotjar/error-handler';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

import { ForwardOption } from './ForwardOption';

const isEmailInputValid = (emailString: string): boolean =>
  validateEmailList(parseEmailListString(emailString)) && emailString.length > 0;

type Email = string;

export interface Settings {
  emails: Array<Email>;
  enabled: boolean;
  valid: boolean;
  dirty: boolean;
}

interface ForwardToEmailProps {
  onChange: (settings: Settings) => void;
  settings: Settings;
  header: React.ReactNode;
  subtext: React.ReactNode;
  trackingState?: string;
}

export const ForwardToEmail = ({
  onChange,
  settings,
  header,
  subtext,
  trackingState,
}: ForwardToEmailProps): JSX.Element => {
  const { dirty } = settings;
  const [isEnabled, setIsEnabled] = useState(settings.enabled);
  const [emails, setEmails] = useState(settings.emails.join(', '));
  const [hasError, setHasError] = useState(false);

  const setEmailErrors = (emailsToValidate: string) =>
    setHasError(!isEmailInputValid(emailsToValidate));

  const { click: trackClick } = useTrack();

  const onToggle = (isChecked: boolean) => {
    if (isChecked) {
      trackClick('Forward Toggle', 'Forward to Email', {
        state_event_was_triggered_on: trackingState,
      });
    }

    setIsEnabled(isChecked);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmails(event.target.value);

    if (hasError) {
      setEmailErrors(event.target.value);
    }
  };

  const onBlur = () => setEmailErrors(emails);

  useEffect(() => {
    if (dirty && !isEmailInputValid(emails)) {
      setHasError(true);
    }
  }, [dirty, emails]);

  useEffect(() => {
    onChange({
      emails: parseEmailListString(emails),
      enabled: isEnabled,
      valid: isEmailInputValid(emails),
      dirty,
    });
  }, [onChange, emails, isEnabled, dirty]);

  const { t } = useTranslation();

  const headerElement = (
    <div>
      <Icon
        css={css`
          margin-right: 10px;
        `}
        icon="email"
        size="m"
      />
      {header}
    </div>
  );

  const inputElement = (
    <>
      {/* Not using the <Field> component since it requires a label which we don't use here. */}
      <TextInput
        placeholder={t('common:forwarding.email.placeholder')}
        value={emails}
        onChange={onInputChange}
        onBlur={onBlur}
        isInvalid={hasError}
        size="s"
      />
      {hasError && (
        <Text as="span" textType="caption" textColor="text_error">
          {t('common:forwarding.email.error')}
        </Text>
      )}
    </>
  );

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <ForwardOption
        isEnabled={isEnabled}
        onChange={onToggle}
        header={headerElement}
        input={inputElement}
        subtext={<div>{subtext}</div>}
      />
    </ErrorBoundary>
  );
};
